/**
 * @file shared/desktop/DesktopUpdateGate.tsx
 * @brief "Please update VERBLIZR" over the Mac app when its shell is older
 *        than MIN_APP_VERSION_MAC (W19, BACKEND B23, 24-10-2026)
 *
 * @details
 * The Mac app is a shell (DESKTOP/, Electron) that loads this web panel, so
 * the page is always new but the shell may not be. The shell says its
 * version through `getAppInfo()` (bridge v4+, `app.getVersion()`, e.g.
 * "0.1.0"). B23 one gate: update when that version < MIN_APP_VERSION_MAC
 * (GET /api/public/config), numerically per part. Fail open: no shell, a
 * shell without getAppInfo (bridge < 4), no minimum, a bad value or no
 * config → nothing shown. "Update now" opens MAC_DOWNLOAD_URL in the real
 * browser (the shell's setWindowOpenHandler). Never set the minimum above
 * the version live on downloads.verblizr.com.
 *
 * Main window only: the shell's own small windows (/desktop/…) are left
 * alone. Mounted once in App; it also loads the public config early for
 * every page (the snapshot plain functions read).
 *
 * Wording = the phone's UpdateRequiredHost (P10).
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FALLBACKS, macUpdateRequired, safeHttps } from "@/shared/publicConfig/publicConfig";
import { usePublicConfig } from "@/shared/publicConfig/usePublicConfig";
import { desktopShell } from "./desktopShell";

/** The shell's version, null when it can't say (fail open). */
function useShellVersion(): string | null {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    const shell = desktopShell();
    if (!shell?.getAppInfo || window.location.pathname.startsWith("/desktop/")) return;
    let live = true;
    shell
      .getAppInfo()
      .then((info) => {
        if (live && info && typeof info.version === "string") setVersion(info.version);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return version;
}

export function MacUpdateScreen({ downloadUrl }: { downloadUrl: string }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mac-update-title"
      className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-background px-4 py-10"
    >
      <div className="flex w-full max-w-md flex-col items-center gap-5 text-center">
        <RefreshCw className="h-16 w-16 text-brand" strokeWidth={1.25} aria-hidden />
        <h1 id="mac-update-title" className="text-2xl font-semibold tracking-tight [text-wrap:balance]">
          Please update VERBLIZR
        </h1>
        <p className="text-sm text-muted-foreground">
          This version of VERBLIZR is no longer supported. Update to keep using the app. Your
          recordings, notes and account are safe.
        </p>
        <Button asChild className="w-full rounded-full">
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
            Update now
          </a>
        </Button>
      </div>
    </div>
  );
}

export function DesktopUpdateGate() {
  const links = usePublicConfig();
  const version = useShellVersion();
  const min = links.optional("MIN_APP_VERSION_MAC");
  if (!macUpdateRequired(version, min)) return null;
  const url = safeHttps(links.value("MAC_DOWNLOAD_URL", FALLBACKS.MAC_DOWNLOAD_URL), FALLBACKS.MAC_DOWNLOAD_URL);
  return <MacUpdateScreen downloadUrl={url} />;
}
